import React, { useCallback, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { CheckoutService } from '../../api/CheckoutService';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, TextField, Typography, Paper, Grid, Button, Link } from '@mui/material';
import { CheckoutItem } from '../../api/protosCompiled/checkout/checkout_pb';
import { emailTemplates } from '../../utils/emailTemplates';
import { EmailTemplate, handleEmail } from './checkoutHelper';
import { useTheme } from '@mui/material/styles';
import { UbAccountService } from '../../api/UbAccountService';
import { SetAccountStatusRequest } from '../../api/protosCompiled/ub_account/ub_account_pb';
import { useCheckoutStore } from '../../stores/checkoutStore';
import { useStore } from 'zustand';

// Load Stripe outside of the component's render to avoid recreating the Stripe object on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? '');

//uncomment for Development (cost $.01 per use)
// const priceIds = [
//   {
//     account: 'price_1RLmgfBIdIe38bPqfGlZc8xz',
//     seat: 'price_1RLmgIBIdIe38bPqbd1XiEhz',
//   },
// ];

//uncomment for Production
const priceIds = [
  {
    account: 'price_1RGPqxBIdIe38bPqmyF3cQdz',
    seat: 'price_1RCTPuBIdIe38bPq1HBkgCeD',
  },
];

interface CheckoutFormProps {
  accountId: string;
  isTrial?: boolean;
}

export const CheckoutForm = ({ accountId, isTrial = false }: CheckoutFormProps) => {
  const seatQuantity = useStore(useCheckoutStore).seatQuantity;
  const setSeatQuantity = useStore(useCheckoutStore).setSeatQuantity;

  const theme = useTheme();

  const fetchClientSecret = useCallback(async () => {
    // Resolve accountId on-demand if not provided via props
    let resolvedAccountId = accountId;
    if (!resolvedAccountId) {
      try {
        const accountResp = await UbAccountService.getAccount();
        resolvedAccountId = accountResp.account?.accountid || '';
      } catch (e) {
        console.error('Unable to resolve account ID for checkout:', e);
      }
    }

    if (!resolvedAccountId) {
      throw new Error('Missing account ID');
    }

    // Create an array of items to purchase
    const items: CheckoutItem.AsObject[] = [
      {
        priceId: priceIds[0].account,
        quantity: 1,
        istrial: isTrial,
        metadata: {
          ubAccountId: resolvedAccountId,
          numSeats: seatQuantity,
        },
      },
    ];

    if (seatQuantity > 0) {
      items.push({ priceId: priceIds[0].seat, quantity: seatQuantity, istrial: isTrial });
    }

    // Store the seat quantity in localStorage before checkout
    localStorage.setItem('checkout_seat_quantity', seatQuantity.toString());

    return CheckoutService.createCheckoutSession(items)
      .then(response => {
        if (!response.clientSecret) {
          throw new Error('Client secret missing in response');
        }
        return response.clientSecret;
      })
      .catch(error => {
        console.error('Error creating checkout session:', error);
        throw error;
      });
    //eslint-disable-next-line
  }, [seatQuantity, accountId]);

  const options = { fetchClientSecret };

  return (
    <Box
      id="checkout"
      sx={{
        maxWidth: 1200, // Adjust maxWidth to fit the screen
        margin: '0 auto',
        padding: 3,
      }}
    >
      <Paper elevation={3} sx={{ padding: 3 }}>
        <Grid container spacing={4} alignItems="flex-start">
          {/* Left Side: Additional Seats Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>
              Additional Seats
            </Typography>
            <Typography variant="body1" gutterBottom>
              Your Unity Build Estimation account comes with <strong>1 seat</strong>. A seat can be
              occupied by one user at a time. You can always purchase additional seats later by
              reaching out to us at{' '}
              <Link
                href="mailto:help@unitybuildestimation.com"
                sx={{
                  color: theme.palette.mode === 'dark' ? '#8eccff' : '#1976d2',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                help@unitybuildestimation.com
              </Link>
              .
            </Typography>
            <TextField
              label="Additional Seats"
              type="number"
              variant="outlined"
              fullWidth
              InputProps={{
                inputProps: { min: 0, max: 5 },
              }}
              value={seatQuantity}
              onChange={e => setSeatQuantity(parseInt(e.target.value, 10) || 0)}
              sx={{ marginTop: 2 }}
            />
          </Grid>

          {/* Right Side: Embedded Checkout */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                maxWidth: '100%',
                height: '75vh', // Changed from 70vh to fixed height to avoid rotation issues
                margin: '0 auto',
                overflowY: 'auto', // Enable vertical scrolling if necessary
              }}
            >
              <EmbeddedCheckoutProvider stripe={stripePromise} options={options} key={seatQuantity}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export const Return = () => {
  // Get a reference to the store - don't use the hook inside a callback
  const checkoutStore = useCheckoutStore;
  const [status, setStatus] = useState<string | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string | null>('');
  const navigate = useNavigate();
  const theme = useTheme();

  // get the welcome email template to send to the customer
  const welcomeEmail: EmailTemplate = emailTemplates.welcome();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    // Get seat quantity from localStorage
    const storedSeatQuantity = localStorage.getItem('checkout_seat_quantity');
    const seatQuantity = storedSeatQuantity ? parseInt(storedSeatQuantity, 10) : 0;

    if (sessionId) {
      CheckoutService.retrieveCheckoutSession(sessionId)
        .then(data => {
          setStatus(data.status);
          setCustomerEmail(data.customerEmail);

          if (data.status === 'complete') {
            handleEmail({
              emailTemplate: welcomeEmail,
              toList: [data.customerEmail],
            });
          }
        })
        .catch(error => {
          console.error('Error retrieving checkout session:', error);
        });

      //update the status of the account to isActive == true and set the number of seats to the number of seats purchased
      const account: SetAccountStatusRequest.AsObject = {
        sessiontoken: '', //we will set this in the service
        isactive: true,
        numseats: seatQuantity + 1, // Include base seat + additional seats
      };
      UbAccountService.setAccountStatus(account);

      // Clear pending subscription flag now that we've returned from checkout
      try {
        localStorage.removeItem('pendingSubscription');
      } catch {}
    }
  }, [navigate, welcomeEmail, checkoutStore]);

  if (status === 'open') {
    return <Navigate to="/checkout" />;
  }

  if (status === 'complete') {
    return (
      <Box
        id="success"
        sx={{
          maxWidth: 600,
          margin: '0 auto',
          padding: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Welcome to Unity-Build Estimation!
        </Typography>

        <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Your Account is Ready
          </Typography>
          <Typography variant="body1" paragraph>
            Thank you for joining Unity-Build Estimation! Your account has been successfully
            created.
          </Typography>
          <Typography variant="body1" paragraph>
            A confirmation email has been sent to <strong>{customerEmail}</strong> with your login
            details.
          </Typography>
          <Typography variant="body1" paragraph>
            You can now log in using your email address and the password you created during signup.
          </Typography>
          <Typography variant="body1" paragraph>
            While we hope you love Unity-Build Estimation, if you need to cancel your subscription,
            please contact us at{' '}
            <Link
              href="mailto:help@unitybuildestimation.com"
              sx={{
                color: theme.palette.mode === 'dark' ? '#8eccff' : '#1976d2',
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              help@unitybuildestimation.com.
            </Link>
          </Typography>
        </Paper>

        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Go to Login
        </Button>

        <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
          Need help? Contact us at{' '}
          <Link
            href="mailto:help@unitybuildestimation.com"
            sx={{
              color: theme.palette.mode === 'dark' ? '#8eccff' : '#1976d2',
              fontWeight: 500,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            help@unitybuildestimation.com
          </Link>
        </Typography>
      </Box>
    );
  }

  return null;
};
